import Channel, { DirectChannel, Management } from '@@types/Channel';
import { CHANNEL_ID, MEMBER_ID, StringTMap } from '@@types/common';
import { ChannelPreference } from '@@types/Preference';
import SelectOption from '@@types/SelectOption';

import {
  isArchivedChannel,
  isDirectChannel,
  isDisplayedChannel,
  isMainThreadChannel,
  isMeChannel,
  isNonArchivedChannel,
  isPrivateChannel,
  isReadSeqNotMatchSeq,
  isSharedChannel,
  isUnreadChannel,
} from '~/services/channelService';
import { api } from '~/stores/providers/Api2';
import { interTenantApi } from '~/stores/providers/InterTenantApi';
import { calcChannelSortPriority } from '~/utils/channelUtil';
import { isNonEmpty, isNonNil, keyBy, mapIndexed } from '~/utils/fnUtil/fnUtil';
import { getPreferenceValueByCategory, isNotificationOn } from '~/utils/settingUtil';
import { isEmpty, noop } from 'lodash';
import { action, comparer, computed, observable } from 'mobx';
import {
  allPass,
  anyPass,
  assoc,
  concat,
  curry,
  descend,
  equals,
  filter,
  find,
  head,
  includes,
  isNil,
  map,
  mergeDeepLeft,
  mergeRight,
  objOf,
  pick,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reject,
  sortWith,
  sum,
  uniq,
  values,
} from 'ramda';
import { Either, Maybe } from 'ramda-fantasy';

import { userStore } from './UserStore';

class ChannelsStore<T extends Channel = Channel> {
  @observable.shallow
  protected data: StringTMap<T> = {};

  preventFetchLogs = false;

  @computed({ equals: comparer.shallow })
  get archivedChannels() {
    return pipe<any, T[], T[]>(filter(isArchivedChannel), sortWith([descend(prop('archivedAt'))]))(this.channels);
  }

  @computed({ equals: comparer.shallow })
  get channels(): Channel[] {
    return sortWith([descend(calcChannelSortPriority)], values(this.data));
  }

  @computed({ equals: comparer.shallow })
  get directChannels(): DirectChannel[] {
    return filter(isDirectChannel, this.channels) as DirectChannel[];
  }

  @computed({ equals: comparer.shallow })
  get privateChannels(): DirectChannel[] {
    return filter(isPrivateChannel, this.channels) as DirectChannel[];
  }

  @computed({ equals: comparer.shallow })
  get meChannel(): Partial<Channel> {
    return find(isMeChannel, this.channels);
  }

  @computed
  get unreadCount(): number {
    return sum(pluck('unreadCount', this.channels));
  }

  @computed({ keepAlive: true, equals: comparer.shallow })
  get unreadChannelsInfo() {
    return pipe(
      mapIndexed((channel, index) => assoc('index', index, channel)),
      filter(isUnreadChannel)
    )(this.recentListChannels);
  }

  @computed
  get unreadMentionCount() {
    return pipe(pluck('mentionCount'), sum)(this.channels);
  }

  @computed({ equals: comparer.shallow })
  get topicChannels(): Channel[] {
    return filter(propSatisfies(isNonEmpty, 'title'), this.privateChannels);
  }

  @computed({ equals: comparer.shallow })
  get nonTopicChannels(): Channel[] {
    return filter(propSatisfies(isEmpty, 'title'), this.privateChannels);
  }

  @computed({ equals: comparer.shallow })
  get displayedChannels(): Channel[] {
    return filter(isDisplayedChannel, this.channels);
  }

  @computed({ equals: comparer.shallow })
  get hiddenChannels() {
    return pipe(reject(isDisplayedChannel), sortWith<T>([descend(prop('displayedChangedAt'))]))(this.channels);
  }

  @computed({ equals: comparer.shallow })
  get recentListChannels() {
    return filter(allPass([isDisplayedChannel, anyPass([isUnreadChannel, isNonArchivedChannel])]), this.channels);
  }

  @computed
  get firstDisplayedSortedChannel(): Channel {
    return head(this.displayedChannels);
  }

  @computed
  get hasUnreadChannel() {
    return isNonNil(find(isUnreadChannel, this.channels));
  }

  @computed
  get doorayNewsChannel() {
    return this.data[userStore.id];
  }

  @computed
  get byOpponentId() {
    // 같은 opponentId인 채널이 2개 이상인 경우 오래된게 최신것을 override하는 현상이 있어서 reverse로 방어코드 추가
    return keyBy('opponentId', this.directChannels.reverse());
  }

  @computed
  get channelSelectOptions(): SelectOption[] {
    return map(
      (channel) => ({ label: channel.channelId, value: channel.channelId }),
      reject<Channel, Channel[]>(isMainThreadChannel, this.channels)
    );
  }

  isChannelNotificationOn = (channelId: string) => {
    return pipe<any, any, any, any, any>(
      this.getById,
      propOr([], 'preferences'),
      getPreferenceValueByCategory('notification'),
      isNotificationOn
    )(channelId);
  };

  getById = (channelId): T => {
    return this.data[channelId];
  };

  @action
  setAdminMembers = curry((channelId, adminIds) => {
    const channel = this.getById(channelId);
    channel.management.adminIds = adminIds;
    this.updateChannel(channel);
  });

  @action
  updateMembers = curry((channelId: CHANNEL_ID, members: MEMBER_ID[]) => {
    this.data[channelId] = mergeRight(this.getById(channelId), { members }) as T;
  });

  getManagementChannelId = (channelId): CHANNEL_ID => {
    return this.data[channelId].management.channelId;
  };

  getByOpponentId = (opponentId: string): T | null => {
    return this.byOpponentId[opponentId] ?? null;
  };

  getFirstChannelIdExcept = (channelId: string) => {
    return pipe(
      reject(propEq('channelId', channelId)),
      sortWith([descend(prop('updatedAt'))]),
      head,
      prop('channelId')
    )(this.recentListChannels);
  };

  safeGetById = (channelId: CHANNEL_ID): Either<CHANNEL_ID, T> => {
    const channel = this.getById(channelId);
    return isNil(channel) ? Either.Left(channelId) : Either.Right(channel);
  };

  fetchPreferences = (channelId: string) => {
    api.me
      .fetchChannelPreferences(channelId)
      .then(objOf('preferences'))
      .then(mergeRight({ channelId }))
      .then(this.setChannelPreferences, noop);
  };

  @action
  setChannelPreferences = (params) => {
    const channel = this.getById(params.channelId);
    if (channel) {
      channel.preferences = params.preferences;
    }
  };

  @action
  setChannelManagement = (management: Management & { channelId: string }) => {
    const channel = this.getById(management.channelId);
    channel.management = management;
    this.updateChannel(channel);
  };

  getAdminMembers = (channelId: string) => {
    return this.getById(channelId)?.management?.adminIds ?? [];
  };

  isOnlyAdmin = (channelId, memberId) => {
    const adminIds = this.getAdminMembers(channelId);
    return adminIds.length === 1 && adminIds[0] === memberId;
  };

  isAdmin = (channelId, memberId) => {
    const adminIds = this.getAdminMembers(channelId);
    return includes(memberId, adminIds);
  };

  @action
  public addChannel = (channel: T) => {
    this.data[channel.channelId] = channel;
  };

  fetchById = curry(async (isSharedChannel: boolean, channelId: string) => {
    const channel = await (isSharedChannel
      ? interTenantApi.channel.fetchChannel(channelId)
      : api.channel.fetchChannel(channelId));
    this.addChannel(channel as T);
    return channel;
  });

  @action
  deleteChannel = (channelId) => {
    delete this.data[channelId];
  };

  @action
  mergeChannels = (channels: T[]) => {
    this.data = mergeRight(this.data, keyBy('channelId', channels));
  };

  @action
  setPreventFetchLogs = (value) => {
    this.preventFetchLogs = value;
  };

  @action
  updateChannel = (channel: Partial<T>) => {
    this.data[channel.channelId] = Object.assign({}, this.data[channel.channelId], channel);
  };

  @action
  appendMembers = (channelId: string, memberIds: string[]) => {
    const channel = this.data[channelId];
    if (!channel) {
      return;
    }
    channel.members = uniq(concat(channel?.members ?? [], memberIds));
    this.updateChannel(channel);
  };

  @action
  removeMember = (channelId: string, memberId: string) => {
    const channel = this.data[channelId];
    if (!channel) {
      return;
    }
    channel.members = reject(equals(memberId), channel.members);
    this.updateChannel(channel);
  };

  @action
  updatePreferences = (data: { channelId: string; preferences: ChannelPreference[] }) => {
    pipe<any, any, any, any, any>(
      this.getById,
      pick(['channelId', 'preferences']),
      mergeDeepLeft(data),
      this.updateChannel
    )(data.channelId);
  };

  safeGetChannelReadSeq = (channelId: string): Maybe<number> => {
    const channel = this.getById(channelId);
    return channel && isReadSeqNotMatchSeq(channel) ? Maybe.Just(channel.readSeq) : Maybe.Nothing();
  };

  fetchHiddenChannels = () => {
    // console.log('fetch hidden channels');
  };

  isSharedChannelId = (channelId: CHANNEL_ID): boolean => {
    return isSharedChannel(this.getById(channelId));
  };
}

export default ChannelsStore;

export const channelsStore = new ChannelsStore();
